import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "@hooks/useTheme";

const Breadcrumb = ({ items, className = "", showBackButton = true, onBackClick, "data-testid": dataTestId }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBackClick = () => {
    if (typeof onBackClick === "function") {
      onBackClick();
      return;
    }

    // Follow the breadcrumb parent instead of history so multi-level paths (e.g. verification detail)
    // still return to the correct screen after refresh or non-linear navigation.
    if (items.length >= 2) {
      const parent = items[items.length - 2];
      if (parent?.href) {
        navigate(parent.href);
        return;
      }
      if (typeof parent?.onClick === "function") {
        parent.onClick();
        return;
      }
    }

    navigate(-1);
  };

  return (
    <nav className={`flex items-center ${className}`} aria-label="Breadcrumb" data-testid={dataTestId}>
      {showBackButton && (
        <button
          onClick={handleBackClick}
          className={`mr-3 flex items text-sm font-medium ${colors.textActive}`}
          aria-label="Go back to previous page"
        >
          <ChevronLeftIcon className="mr-1 h-4 w-4" />
          {t("common.back")}
        </button>
      )}
      {/* Separator */}
      <div className={`w-px h-4 mr-4 ${colors.headerDivider}`}></div>
      <ol className="flex items-center space-x-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && <ChevronRightIcon className={`mx-2 h-4 w-4 ${colors.iconMuted}`} />}
            {item.href ? (
              <Link
                to={item.href}
                className={`text-sm font-medium transition-colors duration-200 ${colors.textActive} truncate max-w-[280px]`}
                title={item.label}
              >
                {item.label}
              </Link>
            ) : item.onClick ? (
              <button
                onClick={item.onClick}
                className={`text-sm font-medium transition-colors duration-200 ${colors.textActive} hover:opacity-80 truncate max-w-[280px]`}
                title={item.label}
              >
                {item.label}
              </button>
            ) : (
              <span className={`text-sm font-medium truncate max-w-[280px] ${colors.textSubTitle}`} title={item.label}>{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
